import * as z from 'zod';
import type { ZodOpenApiPathsObject } from 'zod-openapi';
import { problemResponse } from './common.js';

export const STAFF_ROLES = ['admin', 'senior_manager', 'manager', 'warehouse'] as const;
export const StaffRoleSchema = z.enum(STAFF_ROLES).meta({ id: 'StaffRole' });

/** Unsafe requests from the browser must carry this header (CSRF defence for cookie sessions). */
export const CSRF_HEADER = 'x-deli-csrf';
export const SESSION_COOKIE = 'deli_session';

export const StaffUserSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    email: z.string(),
    role: StaffRoleSchema,
    isActive: z.boolean(),
    lockedUntil: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
  })
  .meta({ id: 'StaffUser' });

export const MeSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    email: z.string(),
    role: StaffRoleSchema,
    permissions: z.array(z.string()),
  })
  .meta({ id: 'Me' });

export const LoginRequestSchema = z
  .object({ email: z.string().min(1).max(254), password: z.string().min(1).max(256) })
  .meta({ id: 'LoginRequest' });

export const ChangeOwnPasswordSchema = z
  .object({ currentPassword: z.string().min(1).max(256), newPassword: z.string().min(1).max(256) })
  .meta({ id: 'ChangeOwnPassword' });

export const CreateUserSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    email: z.string().min(3).max(254),
    role: StaffRoleSchema,
    password: z.string().min(1).max(256),
  })
  .meta({ id: 'CreateUser' });

export const UpdateUserSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    role: StaffRoleSchema.optional(),
    isActive: z.boolean().optional(),
  })
  .meta({ id: 'UpdateUser' });

export const SetPasswordSchema = z
  .object({ password: z.string().min(1).max(256) })
  .meta({ id: 'SetPassword' });

export type StaffUserDto = z.infer<typeof StaffUserSchema>;
export type MeDto = z.infer<typeof MeSchema>;
export type LoginRequest = z.infer<typeof LoginRequestSchema>;
export type CreateUserRequest = z.infer<typeof CreateUserSchema>;
export type UpdateUserRequest = z.infer<typeof UpdateUserSchema>;
export type ChangeOwnPasswordRequest = z.infer<typeof ChangeOwnPasswordSchema>;
export type SetPasswordRequest = z.infer<typeof SetPasswordSchema>;

const json = <T extends z.ZodType>(schema: T) => ({ 'application/json': { schema } });
const body = <T extends z.ZodType>(schema: T) => ({ required: true, content: json(schema) });
const unauthorized = problemResponse('Not signed in or session expired');
const forbidden = problemResponse('Missing permission');
const idParam = { path: z.object({ id: z.uuid() }) };

export const identityPaths: ZodOpenApiPathsObject = {
  '/api/v1/auth/login': {
    post: {
      summary: 'Sign in (sets the session cookie)',
      requestBody: body(LoginRequestSchema),
      responses: {
        '200': { description: 'Signed in', content: json(MeSchema) },
        '400': problemResponse('Invalid request'),
        '401': problemResponse('Wrong email or password, or account locked'),
      },
    },
  },
  '/api/v1/auth/logout': {
    post: { summary: 'Sign out', responses: { '204': { description: 'Signed out' } } },
  },
  '/api/v1/auth/me': {
    get: {
      summary: 'The signed-in user and their permissions',
      responses: { '200': { description: 'OK', content: json(MeSchema) }, '401': unauthorized },
    },
  },
  '/api/v1/auth/password': {
    put: {
      summary: 'Change own password (signs out other sessions)',
      requestBody: body(ChangeOwnPasswordSchema),
      responses: {
        '204': { description: 'Changed' },
        '400': problemResponse('Password does not meet the policy'),
        '401': unauthorized,
      },
    },
  },
  '/api/v1/users': {
    get: {
      summary: 'Staff users',
      responses: {
        '200': { description: 'OK', content: json(z.array(StaffUserSchema)) },
        '401': unauthorized,
        '403': forbidden,
      },
    },
    post: {
      summary: 'Create a staff user',
      requestBody: body(CreateUserSchema),
      responses: {
        '201': { description: 'Created', content: json(StaffUserSchema) },
        '400': problemResponse('Invalid user or password'),
        '401': unauthorized,
        '403': forbidden,
        '409': problemResponse('Email already used'),
      },
    },
  },
  '/api/v1/users/{id}': {
    patch: {
      summary: 'Rename, change role, activate or deactivate',
      requestParams: idParam,
      requestBody: body(UpdateUserSchema),
      responses: {
        '200': { description: 'Updated', content: json(StaffUserSchema) },
        '401': unauthorized,
        '403': forbidden,
        '404': problemResponse('No such user'),
        '422': problemResponse('Would leave no active admin, or deactivate yourself'),
      },
    },
  },
  '/api/v1/users/{id}/password': {
    put: {
      summary: 'Set a new password for a user (unlocks the account, signs them out)',
      requestParams: idParam,
      requestBody: body(SetPasswordSchema),
      responses: {
        '204': { description: 'Set' },
        '400': problemResponse('Password does not meet the policy'),
        '401': unauthorized,
        '403': forbidden,
        '404': problemResponse('No such user'),
      },
    },
  },
};
